import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MenuConfiguration, MenuItem } from '@newgenus/common';
import { MAX_FAVOURITES, allDestinations } from '../../shared/utils/mobile-nav.util';
import { SheetDragDismissDirective } from '@newgenus/angular-shared-components/directives';

export interface MobileNavCustomiseData {
    /** The permission-filtered menu — only destinations the user can reach are offered. */
    menu: MenuConfiguration;
    /** Currently saved favourite ids. */
    selectedIds: string[];
}

/**
 * Favourites picker for the phone bottom bar. The PINNED shortcuts sit at the top
 * and can be dragged to reorder (order = bottom-bar slot order, left→right). The
 * AVAILABLE list below holds everything else the user can reach; tap to pin (up to
 * {@link MAX_FAVOURITES}). Dismisses with the chosen, ordered id array on Save.
 */
@Component({
    selector: 'newgenus-mobile-nav-customise-sheet',
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule,
        MatDividerModule,
        DragDropModule,
        SheetDragDismissDirective,
    ],
    template: `
        <div class="customise-sheet">
            <div class="ng-sheet-grab" ngSheetDrag aria-hidden="true"></div>
            <div class="customise-header">
                <div class="title">
                    <strong>Customise bottom bar</strong>
                    <span class="hint">Drag to reorder · {{ selected.length }}/{{ max }} pinned</span>
                </div>
                <div class="actions">
                    <button mat-button (click)="cancel()">Cancel</button>
                    <button mat-flat-button color="primary" (click)="save()">Save</button>
                </div>
            </div>

            <mat-divider></mat-divider>

            <div class="sheet-scroll">
                <!-- Pinned (top) — draggable to set the bar slot order -->
                <div class="group-label">Pinned shortcuts</div>
                <div class="pinned-list" cdkDropList (cdkDropListDropped)="drop($event)">
                    <div class="row pinned-row" *ngFor="let item of pinnedItems" cdkDrag>
                        <mat-icon class="drag-handle" cdkDragHandle>drag_indicator</mat-icon>
                        <ng-container *ngTemplateOutlet="rowIcon; context: { $implicit: item }"></ng-container>
                        <span class="row-name">{{ item.name }}</span>
                        <button class="row-action" type="button" (click)="unpin(item)" aria-label="Unpin">
                            <mat-icon>close</mat-icon>
                        </button>
                        <div class="drag-preview" *cdkDragPreview>
                            <ng-container *ngTemplateOutlet="rowIcon; context: { $implicit: item }"></ng-container>
                            <span class="row-name">{{ item.name }}</span>
                        </div>
                    </div>
                    <p class="empty" *ngIf="pinnedItems.length === 0">Nothing pinned yet — add shortcuts below.</p>
                </div>

                <!-- Available (below) — tap to pin -->
                <div class="group-label">Available</div>
                <div class="available-list">
                    <button
                        class="row avail-row"
                        type="button"
                        *ngFor="let d of availableItems"
                        (click)="pin(d)"
                        [disabled]="selected.length >= max"
                    >
                        <ng-container *ngTemplateOutlet="rowIcon; context: { $implicit: d }"></ng-container>
                        <span class="row-name">{{ d.name }}</span>
                        <mat-icon class="row-add">add</mat-icon>
                    </button>
                    <p class="empty" *ngIf="availableItems.length === 0">Everything is pinned.</p>
                </div>
            </div>
        </div>

        <ng-template #rowIcon let-item>
            <img *ngIf="item.svg; else matIconTpl" class="row-svg" [attr.src]="'/assets/icons/' + item.svg" alt="" />
            <ng-template #matIconTpl>
                <mat-icon class="row-icon">{{ item.icon || 'chevron_right' }}</mat-icon>
            </ng-template>
        </ng-template>
    `,
    styles: [
        `
            /* System-bar inset comes from the GLOBAL container rule
               (global-styles.scss, DESIGN.md §Sheets) — no self-padding. */
            .customise-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 12px;
                padding: 12px 16px;
            }
            .customise-header .title {
                display: flex;
                flex-direction: column;
                min-width: 0;
            }
            .customise-header .hint {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.55);
            }

            .sheet-scroll {
                max-height: 60vh;
                overflow-y: auto;
                padding: 4px 8px 8px;
            }

            .group-label {
                font-size: 11px;
                text-transform: uppercase;
                letter-spacing: 0.6px;
                font-weight: 700;
                color: rgba(0, 0, 0, 0.45);
                padding: 12px 8px 4px;
            }

            .row {
                display: flex;
                align-items: center;
                gap: 12px;
                width: 100%;
                padding: 10px 8px;
                border-radius: 8px;
                background: transparent;
                border: 0;
                font: inherit;
                color: inherit;
                text-align: left;
                box-sizing: border-box;
            }

            .row-name {
                flex: 1;
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .row-icon,
            .row-add {
                color: #1976d2;
                flex-shrink: 0;
            }
            .row-svg {
                width: 24px;
                height: 24px;
                object-fit: contain;
                flex-shrink: 0;
            }

            .pinned-row {
                background: rgba(25, 118, 210, 0.06);
                margin-bottom: 6px;
                cursor: grab;
            }
            .drag-handle {
                color: rgba(0, 0, 0, 0.35);
                cursor: grab;
                touch-action: none;
                flex-shrink: 0;
            }
            .row-action {
                border: 0;
                background: transparent;
                color: rgba(0, 0, 0, 0.45);
                cursor: pointer;
                display: inline-flex;
                flex-shrink: 0;
                padding: 2px;
            }

            .avail-row {
                cursor: pointer;
            }
            .avail-row:hover:not([disabled]) {
                background: rgba(0, 0, 0, 0.04);
            }
            .avail-row[disabled] {
                opacity: 0.4;
                cursor: default;
            }
            .row-add {
                color: rgba(0, 0, 0, 0.4);
            }

            .empty {
                font-size: 13px;
                color: rgba(0, 0, 0, 0.45);
                padding: 8px;
                margin: 0;
            }

            // CDK drag visuals
            .cdk-drag-preview {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 10px 12px;
                border-radius: 8px;
                background: #fff;
                box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
            }
            .cdk-drag-placeholder {
                opacity: 0.4;
            }
            .pinned-list.cdk-drop-list-dragging .pinned-row:not(.cdk-drag-placeholder) {
                transition: transform 0.18s cubic-bezier(0, 0, 0.2, 1);
            }

            :host-context(.dark-theme) {
                .customise-header .hint,
                .group-label,
                .empty,
                .row-add,
                .row-action {
                    color: rgba(255, 255, 255, 0.6);
                }
                .pinned-row {
                    background: rgba(100, 181, 246, 0.12);
                }
                .avail-row:hover:not([disabled]) {
                    background: rgba(255, 255, 255, 0.06);
                }
                .cdk-drag-preview {
                    background: #2a2a2a;
                }
            }
        `,
    ],
})
export class MobileNavCustomiseSheetComponent {
    public readonly max = MAX_FAVOURITES;
    public destinations: MenuItem[] = [];
    public selected: string[] = [];

    constructor(
        private ref: MatBottomSheetRef<MobileNavCustomiseSheetComponent, string[] | undefined>,
        @Inject(MAT_BOTTOM_SHEET_DATA) public data: MobileNavCustomiseData
    ) {
        this.destinations = allDestinations(this.data.menu || {});
        const availableIds = new Set(this.destinations.map((d) => d.id));
        this.selected = (this.data.selectedIds || []).filter((id) => availableIds.has(id));
    }

    private byId(id: string): MenuItem | undefined {
        return this.destinations.find((d) => d.id === id);
    }

    /** Pinned items in saved order (top of the sheet = left of the bar). */
    public get pinnedItems(): MenuItem[] {
        return this.selected.map((id) => this.byId(id)).filter((i): i is MenuItem => !!i);
    }

    /** Everything the user can reach that isn't pinned. */
    public get availableItems(): MenuItem[] {
        return this.destinations.filter((d) => !this.selected.includes(d.id));
    }

    public drop(event: CdkDragDrop<MenuItem[]>): void {
        moveItemInArray(this.selected, event.previousIndex, event.currentIndex);
    }

    public pin(item: MenuItem): void {
        if (this.selected.length >= this.max || this.selected.includes(item.id)) return;
        this.selected = [...this.selected, item.id];
    }

    public unpin(item: MenuItem): void {
        this.selected = this.selected.filter((id) => id !== item.id);
    }

    public save(): void {
        this.ref.dismiss(this.selected);
    }

    public cancel(): void {
        this.ref.dismiss(undefined);
    }
}
